import { ShieldAlert } from 'lucide-react'
import { Checkbox } from '../../lib/shadcn/checkbox'
import type { GuardrailFlag } from '../../utils/marketing'

/**
 * The production-note guardrails the chosen caption trips.
 *
 * WHY THIS EXISTS AT ALL. The September documents say the same thing six times,
 * in six different places: R1,200 is a target and not guaranteed pay; not
 * guaranteed daily earnings; do not turn the fuel target into an earnings
 * guarantee; progression is a pathway and not a guarantee; reproduce approved
 * rates exactly; expected or illustrative framing only on investor content, no
 * guaranteed-return language.
 *
 * That is the most important instruction in the whole plan and it lives in a
 * Word file that the person writing the caption at six in the evening does not
 * have open. A caption promising a driver R1,200 a shift is a promise Fleetly
 * cannot keep and the driver cannot check.
 *
 * WHY IT DOES NOT BLOCK. A regular expression cannot tell "we do not offer
 * guaranteed returns" from "guaranteed returns", and a check that refuses correct
 * sentences teaches people to write around it, which is worse than no check.
 * These rules were tuned against the eighty real September captions: an earlier,
 * looser version flagged twelve of them and only five deserved it, so the rules
 * were tightened until all eighty pass and a genuine violation still trips.
 *
 * WHAT IT DOES INSTEAD. Every flag has to be ticked before Approve will go
 * through, and the refusal comes from Postgres rather than from this component,
 * so it holds for any future caller. The machine finds, the person decides, and
 * the review row records which flags were in front of them.
 *
 * Read-only mode is used everywhere except the decision box, so the writer sees
 * the same words the approver will, before sending it.
 */
export function GuardrailNotice({
  flags,
  acknowledged,
  onToggle,
}: {
  flags: GuardrailFlag[]
  // Undefined means read-only: shown to whoever is writing, with nothing to tick.
  acknowledged?: string[]
  onToggle?: (code: string) => void
}) {
  if (!flags || flags.length === 0) return null
  const interactive = acknowledged !== undefined && onToggle !== undefined

  return (
    <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-800 dark:bg-amber-950">
      <div className="flex items-center gap-1.5">
        <ShieldAlert className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
        <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
          {flags.length === 1
            ? 'One thing in your plan says to check this caption'
            : `${flags.length} things in your plan say to check this caption`}
        </p>
      </div>

      <ul className="space-y-2">
        {flags.map((f) => (
          <li key={f.code} className="flex gap-2">
            {interactive && (
              <Checkbox
                id={`gr-${f.code}`}
                checked={acknowledged.includes(f.code)}
                onCheckedChange={() => onToggle(f.code)}
                className="mt-0.5 shrink-0"
                aria-label={`I have checked: ${f.label}`}
              />
            )}
            <label
              htmlFor={interactive ? `gr-${f.code}` : undefined}
              className="min-w-0 flex-1 cursor-pointer"
            >
              <p className="text-xs font-medium text-amber-900 dark:text-amber-200">{f.label}</p>
              <p className="mt-0.5 text-[11px] text-amber-900/90 dark:text-amber-200/90">
                {f.message}
              </p>
              {/* The words that tripped it, so nobody has to hunt for them. */}
              {f.excerpt && (
                <p className="mt-0.5 truncate font-mono text-[11px] text-amber-800 dark:text-amber-300">
                  {f.excerpt}
                </p>
              )}
              {/* Where the rule comes from, so a reader can check the claim
                  rather than trust it. A rule nobody can trace gets ignored. */}
              <p className="mt-0.5 text-[10px] text-amber-800/70 dark:text-amber-300/70">
                {f.source_note}
              </p>
            </label>
          </li>
        ))}
      </ul>

      {interactive && (
        <p className="text-[11px] text-amber-900/80 dark:text-amber-200/80">
          Tick each one you have checked. Nothing here is refused, and none of it is a judgement
          about the writing; it is only a second look before a promise goes out.
        </p>
      )}
    </div>
  )
}
